import { InjectionToken } from '@angular/core';
import {
  BooleanMatchStrategy,
  DateMatchStrategy,
  NumberMatchStrategy,
  TextMatchStrategy,
} from './local-handling/filter/row.filter.interface';
import { RowInterface } from './row/row.component';
import type { TableComponent } from './table.component';

export interface TableColumnConfigInterface<T = any> {
  /** Row property this column reads — cell value defaults to `row[key]`. */
  key: keyof T;
  /** Column header text. */
  label?: string;
  /** Cell render format. Also picks a default mapper (e.g. `link` derives `href`, `routerLink` derives `routerLink`). Override via `mapFn`. */
  type?:
    | 'text'
    | 'number'
    | 'date'
    | 'datetime'
    | 'time'
    | 'currency'
    | 'percentage'
    | 'boolean'
    | 'icon'
    | 'image'
    | 'link'
    | 'routerLink'
    | 'links'
    | 'button'
    | 'chips'
    | 'capped-chips'
    | 'boolean-sync'
    | 'switch'
    | 'status'
    | 'count';

  /** URL template for `type: 'link'`. Supports `{rowKey}` placeholders resolved from the row. */
  href?: string;
  /** Anchor target for `type: 'link'`. */
  target?: '_self' | '_blank' | '_parent' | '_top' | '';
  /** Segments template for `type: 'routerLink'`. Supports `{rowKey}` placeholders resolved from the row. */
  routerLink?: string[];
  /** Enables the sort control in the header. */
  sortable?: boolean;
  /** Delay filter form emit until blur instead of each keystroke. */
  filterOnBlur?: boolean;
  /** Custom local sort. Overrides the default comparator. */
  sortComparator?: (a: T, b: T) => number;
  /** Enables the filter control in the header. */
  filterable?: boolean;
  /** Fixed or flexible column width (CSS value). */
  width?: string;
  /** Minimum column width (CSS value). */
  minWidth?: string;
  /** Maximum column width (CSS value). */
  maxWidth?: string;
  /** User cannot hide this column from the column picker. */
  alwaysVisible?: boolean;
  /** Shown by default — user can toggle unless `alwaysVisible`. */
  visible?: boolean;
  /** Line-clamp the cell to N lines (renders as `line-clamp-N`). */
  clamp?: number;
  /** Predefined options for value-based filtering (renders as a dropdown). */
  filterOptions?: { id: string | number; label: string }[];
  /** Comparison strategy for local (client-side) filtering — ignored when the API does the filtering. */
  filterMatchStrategy?: TextMatchStrategy | BooleanMatchStrategy | NumberMatchStrategy | DateMatchStrategy;
  /** API parameter name for this filter when it differs from `key`. */
  filterApiKey?: string;
  /** Never rendered and excluded from the column picker. */
  hidden?: boolean;
  /** Always requested from the API even when not visible — use when another column or `mapFn` depends on its data. */
  alwaysIncluded?: boolean;
  /** Value is derived on the client — column is excluded from the API request. */
  isFrontEndCalculated?: boolean;
  /** Render filter dropdown as single-select instead of multi-select. */
  useSingleSelect?: boolean;
  /** Derive cell fields from the row. Wins over the default `type` mapper when both are set. */
  mapFn?: (column: TableColumnConfigInterface<T>, row: T) => Partial<T>;
}

export interface TableFilterInterface<T = any> {
  key: keyof T;
  value: any;
}

export interface TablePaginationInterface {
  from: number;
  to: number;
  total: number;
}

export interface TableSortInterface<T = any> {
  key: keyof T;
  direction: 'asc' | 'desc' | undefined;
}

export interface TableRowActionInterface<T = any> {
  label: string;
  icon?: string;
  click: (row: T | T[]) => void;
  condition?: (row: T) => boolean;
  /** Greys out (but keeps showing) the action. Static, or evaluated per row. */
  disabled?: boolean | ((row: T) => boolean);
}

export interface TableGroupActionInterface<T = any> {
  label: string;
  icon?: string;
  click: (group: T | T[], table?: TableComponent) => void;
  condition?: (group: T) => boolean;
}

export interface TableBulkActionInterface<T = any> {
  label: string;
  icon?: string;
  click: (group: T | T[], table?: TableComponent) => void;
  condition?: (rows: RowInterface<T>[]) => boolean;
}

export class TableSelectionChange<T = any> {
  constructor(
    public selected: T,
    public added: T,
    public removed: T
  ) {}
}

export class TableVisibleColumnsChange<T = any> {
  constructor(
    public selected: TableColumnConfigInterface<T>[],
    public added: TableColumnConfigInterface<T>[],
    public removed: TableColumnConfigInterface<T>[]
  ) {}
}

export class TableCellSwitchChange<T = any> {
  constructor(
    public row: T,
    public column: TableColumnConfigInterface<T>,
    public value: boolean
  ) {}
}

export class TableShowOnlySelectionChange<T = any> {
  constructor(
    public showOnlySelection: boolean,
    public selectedIds: number[],
    public columns: TableColumnConfigInterface[]
  ) {}
}

// Note: this interface also exists in GroupedData.interface.ts (@campus/dal)
export interface GroupedData<T> {
  [key: string]: any;
  open: boolean;
  children: T[];
  allChildren?: T[];
}

export interface TableRowParentInterface {
  multiple: boolean;
  readonly: boolean;
}

export const CDS_TABLE_ROW_PARENT = new InjectionToken<TableRowParentInterface>('cds-table-row-parent');
